import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useToast } from '@/hooks/use-toast';

interface DeviceSettings {
  showClock: boolean;
  showNewsTicker: boolean;
  showYoutube: boolean;
}

export const useDeviceSettings = (deviceCode?: string) => {
  const [settings, setSettings] = useState<DeviceSettings>({
    showClock: true,
    showNewsTicker: true,
    showYoutube: false
  });
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const fetchSettings = useCallback(async () => {
    if (!deviceCode) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('devices')
        .select('settings')
        .eq('code', deviceCode)
        .single();

      if (error) throw error;

      if (data?.settings && typeof data.settings === 'object' && data.settings !== null) {
        const settingsObj = data.settings as Record<string, any>;
        setSettings({
          showClock: settingsObj.showClock ?? true,
          showNewsTicker: settingsObj.showNewsTicker ?? true,
          showYoutube: settingsObj.showYoutube ?? false
        });
      }
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
    } finally {
      setLoading(false);
    }
  }, [deviceCode]);

  const updateSettings = useCallback(async (newSettings: Partial<DeviceSettings>) => {
    if (!deviceCode) return;

    try {
      setLoading(true);
      
      const updatedSettings = { ...settings, ...newSettings };
      
      const { error } = await supabase
        .from('devices')
        .update({ 
          settings: updatedSettings,
          updated_at: new Date().toISOString()
        })
        .eq('code', deviceCode);

      if (error) throw error;

      setSettings(updatedSettings);

      toast({
        title: "Configurações atualizadas",
        description: "As configurações foram salvas com sucesso"
      });

    } catch (error) {
      console.error('Erro ao atualizar configurações:', error);
      toast({
        title: "Erro ao salvar",
        description: "Não foi possível salvar as configurações",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [deviceCode, settings, toast]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  // Listener para recarregamento automático quando o player for ativado
  useEffect(() => {
    if (!deviceCode) return;

    // Subscrever às mudanças do dispositivo
    const subscription = supabase
      .channel(`device_${deviceCode}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'devices',
          filter: `code=eq.${deviceCode}`
        },
        (payload) => {
          // Sempre que houver update, refetch das configurações
          fetchSettings();
        }
      )
      .subscribe();

    // Cleanup da subscription
    return () => {
      subscription.unsubscribe();
    };
  }, [deviceCode, fetchSettings]);

  return {
    settings,
    loading,
    updateSettings,
    refetch: fetchSettings
  };
};